import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AutomatorDynamicVariableCompoundFilter, AutomatorWorkflowElementVariable } from '@graphql';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-compound-filter-control',
  templateUrl: './compound-filter-control.component.html',
  styleUrls: ['./compound-filter-control.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    TranslateModule,
    MatButtonToggleModule,
    CompoundFilterControlComponent, // Self-reference for recursive usage
    MatIcon,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CompoundFilterControlComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: CompoundFilterControlComponent,
      multi: true,
    },
  ],
})
export class CompoundFilterControlComponent
  implements OnInit, OnDestroy, ControlValueAccessor, Validator, OnChanges
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly changeDetectionRef = inject(ChangeDetectorRef);

  @Input() dynamicVariables: AutomatorWorkflowElementVariable[] = [];
  @Input() level = 1;
  @Input() filterControlTemplateRef!: TemplateRef<any>;
  @Input() formSubmitted = false;
  @Output() remove = new EventEmitter<void>();

  private subscription = new Subscription();

  form!: FormGroup;
  compoundFilters!: FormArray;
  private onChange = (value: any) => value;
  private onTouched = () => {};

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      type: [null, Validators.required],
      filter: [null, Validators.required],
      expression: this.formBuilder.group({
        operator: [null, Validators.required],
        compoundFilters: this.formBuilder.array([], Validators.required),
      }),
    });
    this.compoundFilters = this.form.get('expression.compoundFilters') as FormArray;
    this.form.valueChanges.subscribe((value: AutomatorDynamicVariableCompoundFilter) =>
      this.onChange(value),
    );
    if (this.formSubmitted) {
      this.form.get('type')?.markAsTouched();
      this.form.get('expression.operator')?.markAsTouched();
    }
    setTimeout(() => this.form.updateValueAndValidity(), 0);
  }

  ngOnChanges({ formSubmitted }: SimpleChanges): void {
    if (formSubmitted && formSubmitted.currentValue && this.form) {
      this.form.get('type')?.markAsTouched();
      this.form.get('expression.operator')?.markAsTouched();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.form?.invalid) {
      return { compoundFilterInvalid: true };
    }
    return null;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  writeValue(value: AutomatorDynamicVariableCompoundFilter): void {
    console.log('[CompoundFilterControl] writeValue called with:', value);
    try {
      if (value) {
        const { type, filter, expression } = value;
        console.log(
          '[CompoundFilterControl] Processing value - type:',
          type,
          'filter:',
          filter,
          'expression:',
          expression,
        );

        // Clear existing compound filters first
        const compoundFiltersArray = this.form.get('expression.compoundFilters') as FormArray;
        while (compoundFiltersArray.length !== 0) {
          compoundFiltersArray.removeAt(0);
        }

        this.form.patchValue({
          type,
          filter,
          expression: {
            operator: expression ? expression.operator : undefined,
          },
        });

        if (expression?.compoundFilters) {
          console.log(
            '[CompoundFilterControl] Adding compound filters:',
            expression.compoundFilters.length,
          );
          expression.compoundFilters.forEach((compoundFilter, index) => {
            console.log(
              '[CompoundFilterControl] Adding compound filter',
              index,
              ':',
              compoundFilter,
            );
            this.compoundFilters.push(this.formBuilder.control(compoundFilter));
          });
        }

        if (type === 'EXPRESSION') {
          this.form.get('filter')?.disable();
        } else if (type === 'FILTER') {
          this.form.get('expression')?.disable();
        }

        console.log('[CompoundFilterControl] writeValue completed successfully');
      } else {
        console.log('[CompoundFilterControl] writeValue called with null/undefined value');
        this.onClearFilter();
        this.form.get('type')?.setValue(null);
      }
    } catch (error) {
      console.error('[CompoundFilterControl] Error in writeValue:', error);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onAddExpression(): void {
    console.log('[CompoundFilterControl] onAddExpression called');
    const compoundFilter = this.formBuilder.control({
      type: null,
      filter: null,
      expression: {
        operator: null,
        compoundFilters: [],
      },
    });
    this.form.get('type')?.setValue('EXPRESSION');
    this.form.get('expression')?.enable();
    this.form.get('filter')?.disable();
    this.compoundFilters.push(compoundFilter);
    console.log(
      '[CompoundFilterControl] Added new compound filter, total filters:',
      this.compoundFilters.length,
    );
    this.changeDetectionRef.detectChanges();
  }

  onSetFilter(): void {
    this.form.get('type')?.setValue('FILTER');
    this.form.get('filter')?.enable();
    this.form.get('expression')?.disable();
    this.changeDetectionRef.detectChanges();
  }

  onClearFilter(): void {
    console.log('[CompoundFilterControl] onClearFilter called');
    try {
      this.form.get('type')?.setValue(null);
      this.form.get('filter')?.setValue(null);

      // Clear FormArray properly instead of assigning to readonly controls property
      const compoundFiltersArray = this.form.get('expression.compoundFilters') as FormArray;
      while (compoundFiltersArray.length !== 0) {
        compoundFiltersArray.removeAt(0);
      }

      this.form.reset();
      console.log('[CompoundFilterControl] onClearFilter completed successfully');
    } catch (error) {
      console.error('[CompoundFilterControl] Error in onClearFilter:', error);
    }
  }

  onRemoveClick(): void {
    this.remove.emit();
  }

  onCompoundFilterRemove(index: number): void {
    this.compoundFilters.removeAt(index);
  }
}
